import {FormRepository} from 'domain/ports';
import {Guia, Guide} from 'domain/entities/Guide';
import {Question} from 'domain/entities/Question';
import {Section} from 'domain/entities/Section';
import axios from 'axios';
import {API_BASE_URL, GUIDE_KEYS} from 'constants/app';
import {Scale} from '../../domain';

/**
 * Implementación del repositorio de formularios utilizando la API REST
 */
export class ApiFormRepository implements FormRepository {
    private readonly apiBaseUrl: string;
    private cachedGuides: { [key: string]: Guide } = {};
    private isInitialized: boolean = false;
    private initPromise: Promise<void> | null = null;
    private guidePromises: Partial<Record<string, Promise<void>>> = {};


    // Agregar flags para prevenir bucles infinitos con localStorage
    private isSavingScores: boolean = false;
    private lastSavedAnswers: { [key: string]: { [key: string]: any } } = {};
    private lastSavedScores: { [key: string]: { [key: string]: number } } = {};

    constructor(apiBaseUrl: string = API_BASE_URL) {
        this.apiBaseUrl = apiBaseUrl;
    }

    private cacheKey(guiaKey: string, formKey: string): string {
        const token = localStorage.getItem("user-token") ?? 'SIN_TOKEN';
        return `${token}_${guiaKey}_${formKey}`;
    }

    public init(): Promise<void> {
        return this.initializeFromApi();
    }

    public async forceInit(): Promise<void> {
        // Reinicia la promesa y el estado para forzar reintento
        this.initPromise = null;
        this.isInitialized = false;
        return this.initializeFromApi();
    }

    /**
     * Obtiene una guía específica por su clave
     * @param formKey Clave de la guía (GUIDE_KEYS)
     * @param forceRefresh Si es true, fuerza recarga desde la API (sin usar caché)
     */
    public async getGuideByKey(formKey: string, forceRefresh = false): Promise<Guide> {
        if (!forceRefresh && this.cachedGuides[formKey]) {
            return this.cachedGuides[formKey];
        }

        if (!forceRefresh && this.guidePromises[formKey]) {
            await this.guidePromises[formKey];
            return this.cachedGuides[formKey];
        }

        const loadPromise = (async () => {
            try {
                const guide = await this.fetchGuideByCode(formKey);
                if (guide) {
                    this.cachedGuides[formKey] = guide;
                }
            } catch (err) {
                console.error(`Error al cargar guía ${formKey}:`, err);
                throw err;
            }
        })();

        this.guidePromises[formKey] = loadPromise;
        await loadPromise;
        return this.cachedGuides[formKey];
    }

    /**
     * Obtiene los datos completos de la guía NOM035
     * Implementación sincrónica para compatibilidad con el código existente
     */
    async getGuiaData(): Promise<Guia> {
        // Esperar a que la inicialización se complete si está en curso
        if (!this.isInitialized && this.initPromise) {
            await this.initPromise;
        } else if (!this.isInitialized) {
            await this.initializeFromApi();
        }

        // Retornar los datos de la guía NOM035
        return {
            NOM035: this.cachedGuides
        };
    }

    /**
     * Obtiene las secciones de una guía específica
     * @param formKey Clave de la guía
     */
    async getSections(formKey: string): Promise<Section[]> {
        const guide = await this.getGuideByKey(formKey);
        return guide?.sections || [];
    }

    /**
     * Obtiene las preguntas de una guía específica
     * @param formKey Clave de la guía
     */
    async getQuestions(formKey: string): Promise<Question[]> {
        const guide = await this.getGuideByKey(formKey);
        return guide?.questions || [];
    }

    /**
     * Guarda las respuestas del formulario en localStorage
     * @param guiaKey Clave principal de la guía (NOM035)
     * @param formKey Clave de la guía específica
     * @param answers Respuestas del formulario
     */
    async saveAnswers(guiaKey: string, formKey: string, answers: Record<string, any>): Promise<void> {
        const storageKey = this.cacheKey(guiaKey, formKey);

        // Normaliza: elimina claves con undefined (equivale a limpiar respuesta)
        const normalized: Record<string, any> = {...answers};
        for (const k of Object.keys(normalized)) {
            if (normalized[k] === undefined) delete normalized[k];
        }

        const last = this.lastSavedAnswers[storageKey];
        if (last && JSON.stringify(last) === JSON.stringify(normalized)) {
            console.log('Skipping save – answers unchanged');
            return;
        }

        try {
            // 1) Actualiza cache en memoria coherente con lo persistido
            this.lastSavedAnswers[storageKey] = {...normalized};

            // 2) Persistencia en tokenMap (SOT)
            this.updateTokenMap({formKey, answers: normalized});

            console.log(`Saved answers for ${storageKey} (tokenMap)`);
        } catch (err) {
            console.error('Error saving answers:', err);
        }
    }

    /**
     * Guarda las puntuaciones calculadas en localStorage
     * @param guiaKey Clave principal de la guía
     * @param formKey Clave de la guía específica
     * @param scores Puntuaciones calculadas
     */
    async saveScores(guiaKey: string, formKey: string, scores: { [key: string]: number }): Promise<void> {
        const storageKey = this.cacheKey(guiaKey, formKey);

        if (this.isSavingScores) return;

        // Normaliza: elimina claves con undefined/null si llegaran
        const normalized: Record<string, number> = {};
        for (const [k, v] of Object.entries(scores)) {
            if (v !== undefined && v !== null) normalized[k] = v as number;
        }

        const lastScores = this.lastSavedScores[storageKey];
        if (lastScores && JSON.stringify(lastScores) === JSON.stringify(normalized)) {
            console.log('Skipping save - scores unchanged');
            return;
        }

        this.isSavingScores = true;
        try {
            // 1) Cache coherente
            this.lastSavedScores[storageKey] = {...normalized};

            // 2) Persistencia en tokenMap (SOT)
            this.updateTokenMap({formKey, scores: normalized});

            console.log(`Saved scores for ${storageKey} (tokenMap)`);
        } catch (error) {
            console.error('Error saving scores:', error);
        } finally {
            setTimeout(() => {
                this.isSavingScores = false;
            }, 100);
        }
    }

    updateTokenMap({
                       formKey,
                       answers,
                       scores,
                   }: {
        formKey: string;
        answers?: Record<string, any>;
        scores?: Record<string, number>;
    }) {
        const mapStr = localStorage.getItem("NOM035_tokenMap");
        const map = mapStr ? JSON.parse(mapStr) : {};
        const token = localStorage.getItem("user-token");

        if (!token) {
            return;
        }

        if (!map[token]) {
            map[token] = {answers: {}, scores: {}};
        }

        if (answers) {
            map[token].answers[formKey] = answers;
        }

        if (scores) {
            map[token].scores[formKey] = scores;
        }

        localStorage.setItem("NOM035_tokenMap", JSON.stringify(map));
    }

    /**
     * Obtiene las respuestas guardadas desde localStorage
     * @param guiaKey Clave principal de la guía
     * @param formKey Clave de la guía específica
     */
    async getAnswers(guiaKey: string, formKey: string): Promise<{ [key: string]: any } | null> {
        const storageKey = this.cacheKey(guiaKey, formKey);

        // Usa cache si existe
        if (this.lastSavedAnswers[storageKey]) {
            console.log(`Using cached answers for ${storageKey}`);
            return this.lastSavedAnswers[storageKey];
        }

        try {
            const token = localStorage.getItem("user-token");
            if (!token) return {};

            const mapStr = localStorage.getItem("NOM035_tokenMap");
            const map = mapStr ? JSON.parse(mapStr) : {};
            const fromTokenMap = (map[token]?.answers?.[formKey]) || {};

            // Cachear lo leído desde la SOT
            this.lastSavedAnswers[storageKey] = {...fromTokenMap};

            console.log(`Loaded answers from tokenMap for ${storageKey}`);
            return this.lastSavedAnswers[storageKey];
        } catch (error) {
            console.error('Error loading answers from tokenMap:', error);
            return {};
        }
    }

    /**
     * Obtiene las puntuaciones guardadas desde localStorage
     * @param guiaKey Clave principal de la guía
     * @param formKey Clave de la guía específica
     */
    async getScores(guiaKey: string, formKey: string): Promise<{ [key: string]: number } | null> {
        const storageKey = this.cacheKey(guiaKey, formKey);

        // Usa cache si existe
        if (this.lastSavedScores[storageKey]) {
            console.log(`Using cached scores for ${storageKey}`);
            return this.lastSavedScores[storageKey];
        }

        try {
            const token = localStorage.getItem("user-token");
            if (!token) return {};

            const mapStr = localStorage.getItem("NOM035_tokenMap");
            const map = mapStr ? JSON.parse(mapStr) : {};
            const fromTokenMap = (map[token]?.scores?.[formKey]) || {};

            // Cachear lo leído desde SOT
            this.lastSavedScores[storageKey] = {...fromTokenMap};

            console.log(`Loaded scores from tokenMap for ${storageKey}`);
            return this.lastSavedScores[storageKey];
        } catch (error) {
            console.error('Error loading scores from tokenMap:', error);
            return {};
        }
    }

    /**
     * Inicializa los datos desde la API en segundo plano
     * Esto permite que la aplicación funcione mientras se cargan los datos
     */
    private async initializeFromApi(): Promise<void> {
        if (this.isInitialized) return;
    }

    /**
     * Mapea el DTO de la API a la entidad de dominio Guide
     */
    private mapGuideFromDto(guideDto: any, code: string): Guide {
        const guide: Guide = {
            description: guideDto.description,
            scale: this.mapScaleFromDto(guideDto.responseScales) || {},
            sections: guideDto.sections?.map((sectionDto: any) => this.mapSectionFromDto(sectionDto)) || [],
            questions: []
        };

        // Extraer todas las preguntas de las secciones
        if (guideDto.sections) {
            guide.questions = guideDto.sections.flatMap((section: any) =>
                section.questions?.map((q: any) => this.mapQuestionFromDto(q)) || []
            );
        }

        if (code !== GUIDE_KEYS.I) {
            delete guide.sections;
        }

        // Mapear serviceToClients si existe
        if (guideDto.serviceToClients) {
            guide.serviceToClients = {
                question: guideDto.serviceToClients.question,
                type: guideDto.serviceToClients.type,
                dependentQuestions: guideDto.serviceToClients.dependentQuestions?.map(
                    (q: any) => this.mapQuestionFromDto(q)
                ) || []
            };
        }

        // Mapear isBoss si existe
        if (guideDto.isBoss) {
            guide.isBoss = {
                question: guideDto.isBoss.question,
                type: guideDto.isBoss.type,
                dependentQuestions: guideDto.isBoss.dependentQuestions?.map(
                    (q: any) => this.mapQuestionFromDto(q)
                ) || []
            };
        }

        return guide;
    }

    private generateSectionId(title: string): string {
        const map: Record<string, string> = {
            "Sección I": "GI_I",
            "Sección II": "GI_II",
            "Sección III": "GI_III",
            "Sección IV": "GI_IV"
        };
        return map[title] ?? title.replace(/\s+/g, '_').toUpperCase();
    }

    /**
     * Mapea el DTO de sección a la entidad de dominio Section
     */
    private mapSectionFromDto(sectionDto: any): Section {
        return {
            id: this.generateSectionId(sectionDto.title),
            title: sectionDto.title,
            questions: sectionDto.questions?.map((q: any) => this.mapQuestionFromDto(q)) || []
        };
    }

    /**
     * Transforma un array de escalas de la API al formato Scale
     * @param scaleDtos Array de objetos de escala de la API
     * @returns Objeto Scale con los nombres como claves y los valores numéricos
     */
    private mapScaleFromDto(scaleDtos: Array<{
        id: number;
        name: string;
        description: string;
        options: string[];
    }>): Scale {
        // Si no hay escalas, devolver un objeto vacío
        if (!scaleDtos || !Array.isArray(scaleDtos)) {
            return {};
        }

        // Crear un objeto vacío para el resultado
        const scale: Scale = {};

        // Mapear cada elemento del array a un par clave-valor
        scaleDtos.forEach(scaleItem => {
            // Usar el primer valor de options si existe, si no usar description
            const valueStr = scaleItem.options?.[0] ?? scaleItem.description;
            const value = parseInt(valueStr, 10);

            // Solo agregar si el valor es un número válido
            if (!isNaN(value)) {
                scale[scaleItem.name as keyof Scale] = value;
            } else {
                console.warn(`Valor de escala inválido para '${scaleItem.name}':`, valueStr);
            }
        });

        return scale;
    }

    /**
     * Mapea el DTO de pregunta a la entidad de dominio Question
     */
    private mapQuestionFromDto(questionDto: any): Question {
        return {
            id: questionDto.id,
            text: questionDto.text,
            type: questionDto.type,
            category: questionDto.category,
            domain: questionDto.domain,
            dimension: questionDto.dimension,
            inverted: questionDto.inverted
        };
    }

    /**
     * Obtiene una guía desde la API por su código
     */
    private async fetchGuideByCode(code: string): Promise<Guide | undefined> {
        const response = await axios.get(`${this.apiBaseUrl}/api/guides/${code}`);
        if (response.status === 200) {
            return this.mapGuideFromDto(response.data, code);
        }
        throw new Error(`Error inesperado: status ${response.status}`);
    }
}
